import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import NavBar from "../components/layout/NavBar";
import { HeaderBar } from "../components/layout/TopBar";
import { Hero, SegControl } from "../components/adminSurface/AdminSurface";
import { PulseArt } from "../components/adminSurface/heroArt";
import {
  getMetricsSummary,
  getMetricsTimeline,
  getMetricsEndpoints,
  getMetricsErrors,
  getMetricsDistribution,
  getDbStatus,
} from "../api/adminApi";

/* ─── Design tokens (matches App.css / index.css exactly) ────── */
const T = {
  primary:    "#4f46e5",
  primaryBg:  "#eef2ff",
  green:      "#16a34a",
  greenBg:    "#f0fdf4",
  amber:      "#d97706",
  amberBg:    "#fffbeb",
  red:        "#dc2626",
  redBg:      "#fef2f2",
  blue:       "#2563eb",
  blueBg:     "#eff6ff",
  t900:       "#111827",
  t700:       "#374151",
  t500:       "#6b7280",
  t400:       "#9ca3af",
  t300:       "#d1d5db",
  border:     "#e5e7eb",
  bgPage:     "#f5f6fa",
  bgCard:     "#ffffff",
  bgHover:    "#f9fafb",
  shadow:     "0 2px 8px rgba(79,70,229,0.06), 0 1px 3px rgba(0,0,0,0.05)",
  shadowHover:"0 8px 24px rgba(79,70,229,0.10), 0 2px 6px rgba(0,0,0,0.06)",
  fontSans:   "'Inter', 'system-ui', -apple-system, sans-serif",
};

/* ─── Helpers ─────────────────────────────────────────────────── */
const fmtMs   = (v) => v == null || v === 0 ? "—" : v < 1000 ? `${Math.round(v)} ms` : `${(v/1000).toFixed(2)} s`;
const fmtNum  = (v) => v == null ? "—" : Number(v).toLocaleString();
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "";
const fmtDT   = (iso) => iso ? new Date(iso).toLocaleString() : "";
function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  return `${Math.floor(s/3600)}h ago`;
}
function trendPct(cur, prev) {
  if (!prev || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}

const STATUS_COLORS = { "2xx": T.green, "3xx": T.blue, "4xx": T.amber, "5xx": T.red };
const METHOD_COLORS = [T.primary, T.green, T.amber, T.red, T.blue, T.t400];

/* ─── Card wrapper ────────────────────────────────────────────── */
const Card = ({ children, style }) => (
  <div style={{
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    padding: 24,
    boxShadow: T.shadow,
    transition: "box-shadow .2s, border-color .2s",
    fontFamily: T.fontSans,
    ...style,
  }}>
    {children}
  </div>
);

/* ─── Section heading ─────────────────────────────────────────── */
const SH = ({ children }) => (
  <h2 style={{
    fontSize: 12,
    fontWeight: 700,
    color: T.t400,
    margin: "0 0 16px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontFamily: T.fontSans,
  }}>{children}</h2>
);

/* ─── Sparkline ───────────────────────────────────────────────── */
function Sparkline({ data = [], color = T.primary, w = 68, h = 30 }) {
  if (data.length < 2) return <span style={{ display: "inline-block", width: w }} />;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * (h - 4) - 2}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
                strokeLinejoin="round" strokeLinecap="round" opacity="0.8" />
    </svg>
  );
}

/* ─── KPI card ────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, trend, sparkData, accent = T.primary, warnUp = false }) {
  const hasTrend = trend != null && !isNaN(trend);
  const up       = hasTrend && trend > 0;
  const trendCol = warnUp ? (up ? T.red : T.green) : (up ? T.green : T.red);

  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      borderTop: `3px solid ${accent}`,
      padding: "20px 22px 16px",
      display: "flex",
      flexDirection: "column",
      boxShadow: T.shadow,
      transition: "box-shadow .2s, transform .18s",
      fontFamily: T.fontSans,
      cursor: "default",
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = T.shadowHover; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = T.shadow; e.currentTarget.style.transform = ""; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.t400 }}>
          {label}
        </span>
        {sparkData?.length > 1 && <Sparkline data={sparkData} color={accent} />}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: T.t900, lineHeight: 1, letterSpacing: "-0.02em", marginBottom: 8 }}>
        {value}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minHeight: 18 }}>
        {sub && <span style={{ fontSize: 12, color: T.t400 }}>{sub}</span>}
        {hasTrend && (
          <span style={{ fontSize: 11, fontWeight: 700, color: trendCol, letterSpacing: "0.01em" }}>
            {up ? "↑" : "↓"} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Timeline chart ──────────────────────────────────────────── */
function TimelineChart({ data = [], h = 160 }) {
  const W = 800, H = h;
  const P = { t: 10, r: 44, b: 24, l: 40 };
  if (!data.length) return <Empty h={H} />;

  const cw    = W - P.l - P.r;
  const ch    = H - P.t - P.b;
  const maxR  = Math.max(...data.map(d => d.requestCount), 1);
  const bw    = Math.max(2, cw / data.length - 1);
  const xc    = (i) => P.l + i * (cw / data.length);
  const yR    = (v) => P.t + ch - (v / maxR) * ch;
  const yE    = (v) => P.t + ch - (v / 100) * ch;
  const errPts = data.map((d, i) => `${xc(i) + bw / 2},${yE(d.errorRatePct)}`).join(" ");

  const ticks = [0, Math.floor(data.length / 4), Math.floor(data.length / 2),
                 Math.floor(data.length * 3 / 4), data.length - 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}>
      {/* Gridlines */}
      {[0, 0.5, 1].map(f => {
        const y = P.t + ch * (1 - f);
        return (
          <g key={f}>
            <line x1={P.l} y1={y} x2={W - P.r} y2={y} stroke={T.border} strokeWidth="1" />
            <text x={P.l - 5} y={y + 4} textAnchor="end" fontSize={9} fill={T.t300}>
              {Math.round(maxR * f)}
            </text>
          </g>
        );
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const bh = Math.max(2, (d.requestCount / maxR) * ch);
        return (
          <rect key={i} x={xc(i) + 0.5} y={P.t + ch - bh} width={Math.max(1, bw - 1)} height={bh}
                fill={d.errorCount > 0 ? T.primary : "#c7d2fe"}
                rx="2" />
        );
      })}
      {/* Error rate line */}
      {data.some(d => d.errorRatePct > 0) && (
        <polyline points={errPts} fill="none" stroke={T.red}
                  strokeWidth="2" strokeLinejoin="round" />
      )}
      {/* X labels */}
      {ticks.map(i => (
        <text key={i} x={xc(i) + bw / 2} y={H - 5}
              textAnchor="middle" fontSize={9} fill={T.t400}>
          {fmtTime(data[i]?.t)}
        </text>
      ))}
      {/* Right axis (error %) */}
      {[0, 100].map(v => (
        <text key={v} x={W - P.r + 5} y={yE(v) + 4}
              fontSize={9} fill={T.t400}>{v}%</text>
      ))}
      {/* Legend */}
      <rect x={P.l} y={4} width={10} height={6} fill={T.primaryBg}
            stroke={T.t300} strokeWidth="0.5" rx="1" />
      <text x={P.l + 13} y={10} fontSize={9} fill={T.t500}>Requests</text>
      <line x1={P.l + 70} y1={7} x2={P.l + 82} y2={7}
            stroke={T.red} strokeWidth="2" />
      <text x={P.l + 85} y={10} fontSize={9} fill={T.t500}>Error %</text>
    </svg>
  );
}

/* ─── Donut chart ─────────────────────────────────────────────── */
function Donut({ segments = [], size = 120, centerLabel = "" }) {
  const total = segments.reduce((s, g) => s + g.value, 0);
  if (!total) return <Empty h={size} />;
  const r = 40, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  let off = 0;
  const slices = segments.map(s => {
    const dash = (s.value / total) * circ;
    const sl   = { ...s, dash, off };
    off += dash;
    return sl;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.border} strokeWidth="13" />
      {slices.map((s, i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                stroke={s.color} strokeWidth="13"
                strokeDasharray={`${s.dash} ${circ - s.dash}`}
                strokeDashoffset={circ / 4 - s.off}
                strokeLinecap="round"
                style={{ transform: "rotate(-90deg)", transformOrigin: `${cx}px ${cy}px` }} />
      ))}
      {centerLabel && <>
        <text x={cx} y={cy + 2}  textAnchor="middle" fontSize={14}
              fontWeight="700" fill={T.t900}>{centerLabel}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize={9} fill={T.t400} letterSpacing="0.04em">TOTAL</text>
      </>}
    </svg>
  );
}

/* ─── Donut legend ────────────────────────────────────────────── */
function DonutLegend({ items = [] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14, width: "100%" }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between",
                               alignItems: "center", fontSize: 12.5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3,
                           background: it.color, display: "inline-block", flexShrink: 0 }} />
            <span style={{ color: T.t500, fontWeight: 500 }}>{it.label}</span>
          </div>
          <span style={{ color: T.t900, fontWeight: 700, letterSpacing: "-0.01em" }}>
            {fmtNum(it.count)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Response time histogram ─────────────────────────────────── */
function Histogram({ buckets = [] }) {
  const COLORS = [T.green, T.primary, T.amber, T.red];
  const max   = Math.max(...buckets.map(b => b.count), 1);
  const total = buckets.reduce((s, b) => s + b.count, 0);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 96 }}>
      {buckets.map((b, i) => {
        const pct = total > 0 ? (b.count / total) * 100 : 0;
        const barH = total === 0 ? 4 : Math.max(6, (b.count / max) * 68);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column",
                                alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: COLORS[i], letterSpacing: "-0.01em" }}>
              {Math.round(pct)}%
            </span>
            <div style={{ width: "100%", position: "relative" }}>
              <div style={{ width: "100%", height: barH, background: COLORS[i],
                            borderRadius: "4px 4px 0 0" }} />
            </div>
            <span style={{ fontSize: 10.5, color: T.t400, textAlign: "center",
                           lineHeight: 1.3, whiteSpace: "nowrap", fontWeight: 500 }}>
              {b.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Horizontal bar chart ────────────────────────────────────── */
function HBar({ items = [], unit = "" }) {
  const max = Math.max(...items.map(it => it.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((it, i) => {
        const pct   = (it.value / max) * 100;
        const color = it.value >= 2000 ? T.red : it.value >= 1000 ? T.amber : T.primary;
        const bgColor = it.value >= 2000 ? T.redBg : it.value >= 1000 ? T.amberBg : T.primaryBg;
        return (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 12, color: T.t700, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap",
                             flex: 1, fontFamily: "'JetBrains Mono', 'Fira Mono', monospace",
                             fontWeight: 500, color: T.t700 }}
                    title={it.label}>
                {it.label}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color, flexShrink: 0, letterSpacing: "-0.01em" }}>
                {it.value}{unit}
              </span>
            </div>
            <div style={{ background: bgColor, borderRadius: 4, height: 8 }}>
              <div style={{ width: `${pct}%`, height: "100%", borderRadius: 4,
                            background: color, transition: "width .5s cubic-bezier(.4,0,.2,1)" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Empty state ─────────────────────────────────────────────── */
const Empty = ({ h = 80, msg = "No data yet" }) => (
  <div style={{ height: h, display: "flex", alignItems: "center",
                justifyContent: "center", color: T.t400, fontSize: 13 }}>
    {msg}
  </div>
);

/* ─── Live indicator ──────────────────────────────────────────── */
const LiveDot = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                 fontSize: 11, fontWeight: 700, color: active ? T.green : T.t400,
                 textTransform: "uppercase", letterSpacing: "0.08em",
                 background: active ? T.greenBg : T.bgHover,
                 border: `1px solid ${active ? "#bbf7d0" : T.border}`,
                 borderRadius: 99, padding: "4px 10px" }}>
    <span style={{
      width: 6, height: 6, borderRadius: "50%", display: "inline-block",
      background: active ? T.green : T.t300,
      boxShadow: active ? `0 0 0 2px ${T.greenBg}` : "none",
      animation: active ? "livePulse 2s ease-in-out infinite" : "none",
    }} />
    Live
  </span>
);

/* ─── Status pill ─────────────────────────────────────────────── */
const Pill = ({ text, color, bg }) => (
  <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 99,
                 fontSize: 11, fontWeight: 700, background: bg, color,
                 letterSpacing: "0.02em" }}>
    {text}
  </span>
);

/* ─── Database status card ─────────────────────────────────────
   Surfaces the asynchronous DB bootstrap state from
   /api/admin/db-status. Operators see Bootstrapping/Unreachable
   here while the API itself stays responsive — see
   DatabaseBootstrapHostedService for the retry loop. */
function DbStatusCard({ status, t }) {
  const state    = status?.state ?? "Unknown";
  const isHealthy      = state === "Healthy";
  const isBootstrapping = state === "Bootstrapping" || state === "Pending";
  const isUnreachable  = state === "Unreachable";

  const accent = isHealthy      ? T.green
               : isBootstrapping ? T.amber
               : isUnreachable   ? T.red
                                 : T.t400;
  const accentBg = isHealthy      ? T.greenBg
                 : isBootstrapping ? T.amberBg
                 : isUnreachable   ? T.redBg
                                   : T.bgHover;

  const stateLabel = t(`monitoring.dbStatus.state.${state}`, { defaultValue: state });

  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 14,
      padding: "16px 22px",
      boxShadow: T.shadow,
      fontFamily: T.fontSans,
      display: "flex",
      alignItems: "center",
      gap: 18,
      flexWrap: "wrap",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <span style={{
          width: 10, height: 10, borderRadius: "50%",
          background: accent,
          boxShadow: `0 0 0 3px ${accentBg}`,
        }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: T.t400,
                       textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {t("monitoring.dbStatus.label", { defaultValue: "Database" })}
        </span>
        <Pill text={stateLabel} color={accent} bg={accentBg} />
        {status?.provider && (
          <span style={{ fontSize: 12, color: T.t500, fontWeight: 500 }}>
            {status.provider}
          </span>
        )}
      </div>

      {!isHealthy && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          {status?.retryCount > 0 && (
            <span style={{ fontSize: 12, color: T.t700 }}>
              {t("monitoring.dbStatus.retries", {
                defaultValue: "Retried {{count}} time(s)",
                count: status.retryCount,
              })}
            </span>
          )}
          {status?.lastAttemptUtc && (
            <span style={{ fontSize: 12, color: T.t500 }}>
              {t("monitoring.dbStatus.lastAttempt", { defaultValue: "Last attempt" })}
              {": "}
              {fmtDT(status.lastAttemptUtc)}
            </span>
          )}
        </div>
      )}

      {isUnreachable && status?.lastError && (
        <div style={{
          flex: "1 1 100%",
          marginTop: 4,
          padding: "8px 12px",
          background: T.redBg,
          border: `1px solid #fecaca`,
          borderRadius: 8,
          fontSize: 12,
          color: T.t700,
          fontFamily: "'JetBrains Mono', 'Fira Mono', monospace",
          wordBreak: "break-word",
        }}>
          <span style={{ fontWeight: 700, marginRight: 6 }}>
            {t("monitoring.dbStatus.lastError", { defaultValue: "Last error" })}:
          </span>
          {status.lastError}
        </div>
      )}

      {isHealthy && status?.completedUtc && (
        <span style={{ fontSize: 12, color: T.t500, marginLeft: "auto" }}>
          {t("monitoring.dbStatus.connectedAt", { defaultValue: "Connected at" })}
          {": "}
          {fmtDT(status.completedUtc)}
        </span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
export default function MonitoringDashboard() {
  const { t }     = useTranslation();
  const { token } = useAuth();

  const [hours,       setHours]       = useState(1);
  const [summary,     setSummary]     = useState(null);
  const [timeline,    setTimeline]    = useState([]);
  const [endpoints,   setEndpoints]   = useState([]);
  const [errors,      setErrors]      = useState([]);
  const [dist,        setDist]        = useState(null);
  const [dbStatus,    setDbStatus]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [epSort,      setEpSort]      = useState("callCount");
  const [epDir,       setEpDir]       = useState("desc");
  const timerRef = useRef(null);

  const buckets = hours <= 1 ? 5 : hours <= 6 ? 15 : 60;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    // db-status is anonymous and must succeed even when the rest of the API
    // is failing (DB outage), so request it independently.
    getDbStatus().then(setDbStatus).catch(() => setDbStatus(null));
    try {
      const [s, tl, ep, er, d] = await Promise.all([
        getMetricsSummary(token, hours),
        getMetricsTimeline(token, hours, buckets),
        getMetricsEndpoints(token, hours),
        getMetricsErrors(token, 30),
        getMetricsDistribution(token, hours),
      ]);
      setSummary(s);
      setTimeline(Array.isArray(tl) ? tl : []);
      setEndpoints(Array.isArray(ep) ? ep : []);
      setErrors(Array.isArray(er) ? er : []);
      setDist(d);
      setLastRefresh(new Date());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, hours, buckets]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timerRef.current = setInterval(load, 30_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  /* Derived */
  const cur  = summary?.current ?? {};
  const prev = summary?.prev    ?? {};

  const sparkReq = timeline.map(b => b.requestCount);
  const sparkErr = timeline.map(b => b.errorRatePct);
  const sparkMs  = timeline.map(b => b.avgDurationMs);

  const statusSegs  = (dist?.statusGroups ?? []).map(g => ({
    label: g.group, value: g.count, color: STATUS_COLORS[g.group] ?? T.t400,
  }));
  const methodSegs  = (dist?.methods ?? []).map((m, i) => ({
    label: m.method, value: m.count, color: METHOD_COLORS[i % METHOD_COLORS.length],
  }));

  const sortedEp = [...endpoints].sort((a, b) => {
    const va = a[epSort] ?? 0, vb = b[epSort] ?? 0;
    return epDir === "desc" ? vb - va : va - vb;
  });

  const toggleSort = (col) => {
    if (epSort === col) setEpDir(d => d === "desc" ? "asc" : "desc");
    else { setEpSort(col); setEpDir("desc"); }
  };
  const si = (col) => epSort !== col ? " ↕" : epDir === "desc" ? " ↓" : " ↑";

  const errorRate     = cur.errorRatePct ?? 0;
  const errAccent     = errorRate >= 5 ? T.red : errorRate >= 1 ? T.amber : T.green;
  const p95Accent     = (cur.p95DurationMs ?? 0) >= 1000 ? T.amber : T.primary;

  /* Table header cell */
  const TH = ({ label, col, right }) => (
    <th onClick={() => col && toggleSort(col)}
        style={{ padding: "9px 12px", textAlign: right ? "right" : "left",
                 fontSize: 11, fontWeight: 600, color: T.t500, letterSpacing: "0.05em",
                 textTransform: "uppercase", cursor: col ? "pointer" : "default",
                 userSelect: "none", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}` }}>
      {label}{col ? si(col) : ""}
    </th>
  );

  const RANGE = [
    { label: t("monitoring.timeRange1h",  { defaultValue: "Last 1h"  }), v: 1  },
    { label: t("monitoring.timeRange6h",  { defaultValue: "Last 6h"  }), v: 6  },
    { label: t("monitoring.timeRange24h", { defaultValue: "Last 24h" }), v: 24 },
  ];

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container adm">

        {/* ── Header ─────────────────────────────────────── */}
        <Hero
          title={t("monitoring.title", { defaultValue: "API Monitoring" })}
          subtitle={t("monitoring.subtitle", { defaultValue: "Live request metrics, error rates and response times." })}
          logo={<PulseArt />}
          onRefresh={load}
          refreshing={loading}
          actions={
            <>
              <LiveDot active={!loading} />
              {lastRefresh && (
                <span style={{ fontSize: 12, color: "rgba(255,255,255,.8)" }}>{timeAgo(lastRefresh.toISOString())}</span>
              )}
              <SegControl
                value={hours}
                onChange={setHours}
                options={RANGE.map(o => ({ value: o.v, label: o.label }))}
              />
            </>
          }
        />

        {/* ── Database status ────────────────────────────── */}
        <section className="adm-section">
          <DbStatusCard status={dbStatus} t={t} />
        </section>

        {/* ── KPI Cards ──────────────────────────────────── */}
        <section className="adm-section">
          <div className="adm-overview-grid" style={{ gap: 20 }}>
            <KpiCard
              label={t("monitoring.requestsPerMin", { defaultValue: "Requests / min" })}
              value={cur.requestsPerMin?.toFixed(2) ?? "—"}
              sub={`${fmtNum(cur.totalRequests)} total`}
              trend={trendPct(cur.totalRequests, prev.totalRequests)}
              sparkData={sparkReq}
              accent={T.primary}
            />
            <KpiCard
              label={t("monitoring.errorRate", { defaultValue: "Error Rate" })}
              value={`${errorRate}%`}
              sub={`${fmtNum(cur.errorCount)} errors`}
              trend={trendPct(cur.errorRatePct, prev.errorRatePct)}
              sparkData={sparkErr}
              accent={errAccent}
              warnUp
            />
            <KpiCard
              label={t("monitoring.avgResponseTime", { defaultValue: "Avg Response" })}
              value={fmtMs(cur.avgDurationMs)}
              sub=""
              trend={trendPct(cur.avgDurationMs, prev.avgDurationMs)}
              sparkData={sparkMs}
              accent={T.amber}
              warnUp
            />
            <KpiCard
              label={t("monitoring.p95ResponseTime", { defaultValue: "P95 Response" })}
              value={fmtMs(cur.p95DurationMs)}
              sub={(cur.p95DurationMs ?? 0) >= 1000 ? "Above 1 s" : "Within target"}
              sparkData={[]}
              accent={p95Accent}
              warnUp
            />
          </div>
        </section>

        {/* ── Timeline + Status codes ─────────────────────── */}
        <section className="adm-section">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 20 }}>
            <Card>
              <SH>{t("monitoring.timeline", { defaultValue: "Request Volume & Error Rate" })}</SH>
              <TimelineChart data={timeline} />
            </Card>

            <Card>
              <SH>Status Codes</SH>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Donut
                  segments={statusSegs}
                  size={124}
                  centerLabel={fmtNum(cur.totalRequests)}
                />
                <DonutLegend items={statusSegs.map(s => ({
                  label: s.label, color: s.color, count: s.value,
                }))} />
              </div>
            </Card>
          </div>
        </section>

        {/* ── Three mini widgets ──────────────────────────── */}
        <section className="adm-section">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>

            <Card>
              <SH>Response Time Distribution</SH>
              <Histogram buckets={dist?.timeBuckets ?? []} />
            </Card>

            <Card>
              <SH>HTTP Methods</SH>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Donut segments={methodSegs} size={112} />
                <DonutLegend items={methodSegs.map(m => ({
                  label: m.label, color: m.color, count: m.count,
                }))} />
              </div>
            </Card>

            <Card>
              <SH>Top 5 Slowest Endpoints (P95)</SH>
              {!(dist?.slowEndpoints?.length)
                ? <Empty />
                : <HBar
                    items={(dist.slowEndpoints).map(e => ({
                      label: e.endpoint,
                      value: e.p95Ms,
                    }))}
                    unit=" ms"
                  />
              }
            </Card>
          </div>
        </section>

        {/* ── Endpoint breakdown ──────────────────────────── */}
        <section className="adm-section">
          <SH>{t("monitoring.endpoints", { defaultValue: "Endpoint Breakdown" })}</SH>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.bgHover }}>
                    <TH label={t("monitoring.endpoint", { defaultValue: "Endpoint" })} />
                    <TH label="Calls"    col="callCount"     right />
                    <TH label="Errors"   col="errorCount"    right />
                    <TH label="Error %"  col="errorRatePct"  right />
                    <TH label="Avg (ms)" col="avgDurationMs" right />
                    <TH label="P95 (ms)" col="p95DurationMs" right />
                    <TH label="Max (ms)" col="maxDurationMs" right />
                  </tr>
                </thead>
                <tbody>
                  {sortedEp.length === 0
                    ? <tr><td colSpan={7}><Empty h={64} /></td></tr>
                    : sortedEp.map((ep, i) => {
                        const errHigh = ep.errorRatePct >= 10;
                        const errMid  = ep.errorRatePct >= 5;
                        const slow    = ep.avgDurationMs >= 1000;
                        return (
                          <tr key={i} style={{
                            borderBottom: `1px solid ${T.bgHover}`,
                            background: errHigh ? "#fff9f9" : slow ? "#fffcf0" : T.bgCard,
                          }}>
                            <td style={{ padding: "9px 12px", maxWidth: 340 }}>
                              <span style={{ fontFamily: "monospace", fontSize: 12,
                                             color: T.t700, display: "block",
                                             overflow: "hidden", textOverflow: "ellipsis",
                                             whiteSpace: "nowrap" }}
                                    title={ep.endpoint}>
                                {ep.endpoint}
                              </span>
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right",
                                         color: T.t700, fontWeight: 500 }}>
                              {ep.callCount}
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right",
                                         color: ep.errorCount > 0 ? T.red : T.t400,
                                         fontWeight: ep.errorCount > 0 ? 600 : 400 }}>
                              {ep.errorCount}
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right" }}>
                              <Pill
                                text={`${ep.errorRatePct}%`}
                                color={errHigh ? T.red : errMid ? T.amber : T.green}
                                bg={errHigh ? T.redBg : errMid ? T.amberBg : T.greenBg}
                              />
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right",
                                         color: ep.avgDurationMs >= 1000 ? T.amber : T.t700 }}>
                              {ep.avgDurationMs}
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right",
                                         color: ep.p95DurationMs >= 2000 ? T.red
                                              : ep.p95DurationMs >= 1000 ? T.amber : T.t700 }}>
                              {ep.p95DurationMs}
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right",
                                         color: T.t500 }}>
                              {ep.maxDurationMs}
                            </td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        {/* ── Recent errors ───────────────────────────────── */}
        <section className="adm-section">
          <SH>{t("monitoring.recentErrors", { defaultValue: "Recent Errors" })}</SH>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {errors.length === 0
              ? <Empty h={64} msg={t("monitoring.noErrors", { defaultValue: "No errors in this period" })} />
              : <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.bgHover }}>
                        {["Time", "Status", "Endpoint", "Duration", "Message"].map(h => (
                          <th key={h} style={{ padding: "9px 12px", textAlign: "left",
                                               fontSize: 11, fontWeight: 600, color: T.t500,
                                               letterSpacing: "0.05em", textTransform: "uppercase",
                                               whiteSpace: "nowrap",
                                               borderBottom: `2px solid ${T.border}` }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((e, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.bgHover}`,
                                             background: i % 2 === 0 ? T.bgCard : T.bgHover }}>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 12, color: T.t700 }}>{timeAgo(e.timestamp)}</div>
                            <div style={{ fontSize: 11, color: T.t400 }}>{fmtDT(e.timestamp)}</div>
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            <Pill
                              text={e.statusCode}
                              color={e.statusCode >= 500 ? T.red  : T.amber}
                              bg={e.statusCode   >= 500 ? T.redBg : T.amberBg}
                            />
                          </td>
                          <td style={{ padding: "9px 12px", maxWidth: 280 }}>
                            <span style={{ fontFamily: "monospace", fontSize: 12,
                                           color: T.t700, display: "block",
                                           overflow: "hidden", textOverflow: "ellipsis",
                                           whiteSpace: "nowrap" }}
                                  title={e.endpoint}>
                              {e.endpoint}
                            </span>
                          </td>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap",
                                       color: e.durationMs >= 1000 ? T.amber : T.t500,
                                       fontSize: 12 }}>
                            {fmtMs(e.durationMs)}
                          </td>
                          <td style={{ padding: "9px 12px", fontSize: 12, color: T.t500,
                                       maxWidth: 300, overflow: "hidden",
                                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={e.errorMessage}>
                            {e.errorMessage || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </Card>
        </section>

      </div>
    </div>
  );
}
